import { useCallback, useRef } from "react";
import { EVENTS } from "@bash/shared";
import { useConnectionStore } from "@/stores/connectionStore";

// Mirror the classic task input's typing throttle (task/layout.tsx
// preWarmSandbox) so the two paths warm on the same cadence.
const TYPING_SESSION_GAP_MS = 1500;
const TYPING_TOUCH_THROTTLE_MS = 1000; // ~1Hz keep-warm heartbeat

/**
 * useComposerPrewarm — warm the sandbox WHILE the user types in the home
 * composer, the parity the classic task input has always had but the composer
 * never did. Pre-composer, the classic input fired SANDBOX_PRE_WARM on typing so
 * send-time latency was just the model; the composer path started the wake only
 * after send → router turn → create_task, so a cold sandbox / 502 storm was paid
 * in full at send time.
 *
 * Reuses the SAME server mechanism (SANDBOX_PRE_WARM + the SANDBOX_TYPING
 * keep-warm touch) — no new wake path. Fires with NO taskId: a composer ask is
 * born with a fresh server-side task id (create_task → randomUUID), so a
 * task-specific WarmQuery would never be consumed. The server's no-taskId branch
 * is purpose-built for this ("user typing on a blank canvas"): it wakes the
 * runtime and marks it warm-ready. The first real message still pays the SDK
 * spawn (~2-3s), but the expensive Blaxel resume / WS open is already done.
 *
 * Fire-and-forget: no UI dependency, no error surfacing. Safe when already warm —
 * the server treats a live sandbox as a no-op (presence gate + prewarmLocks
 * dedup), and the once-per-typing-session gate below keeps a burst of keystrokes
 * to a single wake.
 *
 * NOTE: intentionally a small standalone hook rather than a refactor of the
 * classic preWarmSandbox — keeping the classic path untouched avoids any
 * regression there. The shared contract is the server event, not this code.
 */
export function useComposerPrewarm(): () => void {
  const lastKeystrokeAt = useRef(0);
  const lastTypingTouchAt = useRef(0);

  return useCallback(() => {
    const now = Date.now();

    // ~1Hz keep-warm touch — the server forwards it to the sandbox as an activity
    // touch ONLY when the relay is live (dead relays drop it), deferring the idle
    // timer so a warm sandbox stays hot while the user keeps typing.
    if (now - lastTypingTouchAt.current >= TYPING_TOUCH_THROTTLE_MS) {
      lastTypingTouchAt.current = now;
      useConnectionStore.getState().getSocket()?.emit(EVENTS.SANDBOX_TYPING);
    }

    // The actual wake fires once per typing session (a new burst after a
    // >1.5s pause); intermediate keystrokes are a no-op after the touch above.
    const isNewSession = now - lastKeystrokeAt.current > TYPING_SESSION_GAP_MS;
    lastKeystrokeAt.current = now;
    if (!isNewSession) return;

    // Don't fire when a wake is already in flight or the sandbox is busy —
    // mirrors the classic gate; the server also no-ops an already-warm sandbox.
    const status = useConnectionStore.getState().connectionState.status;
    if (status === "active" || status === "starting" || status === "updating")
      return;

    useConnectionStore
      .getState()
      .getSocket()
      ?.emit(EVENTS.SANDBOX_PRE_WARM, {});
  }, []);
}
